import { Link } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { Code2, ExternalLink, TrendingUp, Flame, Shield, DollarSign } from 'lucide-react'
import { Badge } from '@/components/ui'
import { cn, entityColor, statusColor, formatCurrency, formatRelativeDate } from '@/lib/utils'
import { computeScore } from '@/lib/scoring'
import type { AppRecord } from '@/lib/types'

export function AppCard({ app, index = 0 }: { app: AppRecord; index?: number }) {
  const score = app.score ?? computeScore(app)
  const eColor = entityColor(app.entity)
  const sColor = statusColor(app.status)
  const v = app.valuation?.acquisition.midpoint

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(0.4, index * 0.03) }}
    >
      <Link
        to="/apps/$slug"
        params={{ slug: app.slug }}
        className="block group"
      >
        <div className="glass rounded-lg p-5 hover:border-gold/40 transition-all duration-200 h-full flex flex-col">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1.5">
                <span className={cn('eyebrow', eColor.text)}>{app.entity}</span>
                <span className="font-mono text-[9px] text-cream/30">·</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-cream/45">
                  {app.category}
                </span>
              </div>
              <h3 className="font-heading text-lg text-cream tracking-tight truncate group-hover:text-gold-soft transition-colors">
                {app.name}
              </h3>
            </div>
            <span className={cn('flex items-center gap-1.5 shrink-0 px-2 py-0.5 rounded-md border text-[10px] uppercase tracking-[0.12em] font-mono', sColor.bg, sColor.text)}>
              <span className={cn('size-1 rounded-full', sColor.dot)} />
              {app.status}
            </span>
          </div>

          <p className="font-body text-sm text-cream/65 leading-relaxed line-clamp-2 mb-4 flex-1">
            {app.description}
          </p>

          <div className="grid grid-cols-4 gap-2 mb-4">
            <ScorePill icon={<Flame size={11} />} label="HOT" value={score.heat} max={100} tone="gold" />
            <ScorePill icon={<TrendingUp size={11} />} label="TRD" value={score.trend} max={100} tone="bronze" />
            <ScorePill icon={<DollarSign size={11} />} label="VAL" value={score.value} max={100} tone="success" />
            <ScorePill icon={<Shield size={11} />} label="DEF" value={score.defensibility} max={100} tone="info" />
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-cream/8 text-[11px]">
            <div className="flex items-center gap-2 text-cream/55 font-mono uppercase tracking-[0.1em]">
              {app.github_repo && <Code2 size={12} />}
              {app.firebase_site && <ExternalLink size={12} />}
              <span>{formatRelativeDate(app.last_modified)}</span>
            </div>
            <div className="font-mono text-gold-soft">
              {v ? formatCurrency(v, { compact: true }) : '—'}
            </div>
          </div>

          {app.stack?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {app.stack.slice(0, 4).map((s) => (
                <Badge key={s} tone="bronze" className="!py-0">{s}</Badge>
              ))}
              {app.stack.length > 4 && (
                <span className="font-mono text-[9px] text-cream/40 self-center">+{app.stack.length - 4}</span>
              )}
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  )
}

function ScorePill({ icon, label, value, max, tone }: {
  icon: React.ReactNode
  label: string
  value: number
  max: number
  tone: 'gold' | 'bronze' | 'success' | 'info'
}) {
  const pct = Math.min(100, (value / max) * 100)
  const toneCls = {
    gold: 'text-gold-soft',
    bronze: 'text-bronze',
    success: 'text-success',
    info: 'text-info',
  }[tone]
  const barCls = {
    gold: 'bg-gold-soft',
    bronze: 'bg-bronze',
    success: 'bg-success',
    info: 'bg-info',
  }[tone]
  return (
    <div className="rounded-md bg-navy/40 border border-cream/5 p-2">
      <div className={cn('flex items-center justify-between mb-1.5 font-mono text-[9px] uppercase tracking-[0.1em]', toneCls)}>
        <span className="flex items-center gap-1">{icon}{label}</span>
        <span>{value}</span>
      </div>
      <div className="h-1 rounded-full bg-cream/8 overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', barCls)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}
